import { ImageName } from "@mcleod/images";
import { Button, ButtonProps, ButtonVariant, Component, HorizontalSpacer, Image, ImageProps, Layout, MainPageAccessor, Panel, PanelProps, TabStyle } from "@mcleod/components";
import { Alignment, CompanySettings, DeviceAccessor, getThemeColor, HorizontalAlignment } from "@mcleod/core";
import { AppTabset } from "../tabset/AppTabset";
import { AppTab } from "../tabset/AppTab";

export interface AppHeaderActionPanels {
    layout: Layout;
    addlPanelLeft: Panel;
    addlPanelRight: Panel;
    panelTools?: Panel;
    // TODO handle context menu actions defined in panel tools' ellipsis.
}

const semiTransparent = '#FFFFFFEE';

export class AppHeaderActions extends Panel {
    private _contextButton: Button;
    private header: AppHeaderActionPanels;
    private readonly _panelActionsMain: Panel;
    private readonly _panelActionsHeader: Panel;
    private _appSpecificActionPanel: Panel;

    constructor(header: AppHeaderActionPanels) {
        super({
            minWidth: DeviceAccessor.isTablet ? (DeviceAccessor.orientationAccessor.isLandscape ? "20%" : "40%") : "75%",
            height: actionPanelHeight(),
            align: HorizontalAlignment.RIGHT,
            backgroundColor: semiTransparent,
            borderColor: "subtle.lightest",
            borderWidth: 3,
            margin: 12,
            borderRadius: 12,
            padding: 0
        });
        this.header = header;

        this._panelActionsHeader = new Panel({
            id: "panelActionsHeader",
            padding: 12,
            fillRow: true,
            align: HorizontalAlignment.LEFT
        });
        this._panelActionsMain = new Panel({
            id: "panelActionsMain", fillRow: true, scrollY: true
        });

        this.add(this._panelActionsHeader);
        this.add(this._panelActionsMain);
        this.addClickListener(() => this.hideContextActions());
    }

    private get appSpecificActionPanel(): Panel {
        if (this._appSpecificActionPanel == null) {
            this._appSpecificActionPanel = new Panel({
                id: "appSpecificActionPanel",
                fillRow: true,
                rowBreak: true,
                padding: 0,
                margin: 0
            });
        }
        return this._appSpecificActionPanel;
    }

    public addAppAction(button: Button): void {
        this.appSpecificActionPanel.add(button);
    }

    public addHeaderContent(component: Component) {
        this._panelActionsHeader.add(component);
    }

    public get contextButton(): Button {
        if (this._contextButton == null) {
            this._contextButton = new Button({
                id: "btnHeaderActionMenu",
                backgroundColor: "transparent",
                variant: ButtonVariant.round,
                imageWidth: 20,
                imageHeight: 20,
                imageName: ImageName.ellipsis,
                imageColor: CompanySettings.get().text_color ?? getThemeColor("primary.reverse"),
                imageRotation: 90,
                caption: null,
                width: null,
                height: null,
                padding: 0,
                paddingTop: 4,
                margin: 0,
                onClick: () => { this.onContextButtonClick(); }
            });
        }
        return this._contextButton;
    }

    private onContextButtonClick(): void {
        this.syncActions();
        this.setProps({ // In the even that the orientation changes.
            minHeight: actionPanelHeight(),
            maxHeight: actionPanelHeight(),
            height: actionPanelHeight()
        });
        this.displayContextActions();
    }

    private displayContextActions() {
        this.slideIn({
            ...transitionOptions,
            coverPageHeader: false
        }, true, {
            closeOnClickOff: true,
            greyedBackground: true,
            coverPageHeader: true,
            onClose: () => this.hideContextActions()
        });
    }

    private hideContextActions() {
        this.slideOut(transitionOptions);
    }

    private get actionPanels(): Panel[] {
        const result: Panel[] = [this.header.addlPanelLeft, this.appSpecificActionPanel, this.header.addlPanelRight];
        const tabPanels = this.tabSectionHeadingPanels;
        if (tabPanels != null && tabPanels.length > 0) {
            result.push(...this.tabSectionHeadingPanels);
        }
        if (this.panelTools != null) {
            result.push(this.panelTools);
        }
        return result;
    }

    private get tabSectionHeadingPanels(): Panel[] | null {
        const layout = this.header.layout;
        if (layout == null) return null;
        const tabset = layout.findComponentByType(AppTabset);
        if (tabset == null || tabset.originalTabStyle !== TabStyle.SCROLL) return null;

        const result: Panel[] = [];
        const tabs: AppTab[] = layout.findComponentsByType(AppTab);
        tabs.forEach((tab: AppTab) => {
           const panel = tab["_addlTitleCompsPanel"];
           if (panel == null || panel.components == null || panel.components.length == 0)
               return;
           else
               result.push(panel);
        });
        return result;
    }

    private get panelTools(): Panel | null {
        return this.header.panelTools ?? null;
    }

    public syncActions() {
        this._panelActionsMain.removeAll();

        for (const panel of this.actionPanels) {
            this.addAppActionSubPanel(panel);
        }
    }

    public get hasActions(): boolean {
        return this.actionPanels?.some((panel: Panel) => this.hasVisibleHeaderActions(panel));
    }

    private hasVisibleHeaderActions(panel: Panel): boolean {
        return panel?.components?.some((value) => value.visible);
    }

    private addAppActionSubPanel(panel: Panel, showSeparator: boolean = false) {
        if (this.hasVisibleHeaderActions(panel)) {
            panel.components.forEach((c: Component) => {
                this.applyAppHeaderActionComponentProps(c, panel);
            });
            panel.setProps(menuSubPanelProps);
            this._panelActionsMain.add(panel);
            if (showSeparator)
                this._panelActionsMain.add(new HorizontalSpacer({
                    marginLeft: 8, marginRight: 8, borderBottomWidth: 1, borderBottomColor: "default.lightest"
                }));
        }
    }

    private applyAppHeaderActionComponentProps(c: Component, originPanel: Panel) {
        if (c instanceof Panel) {
            c.components.forEach((comp: Component) => {
                this.applyAppHeaderActionComponentProps(comp, c);
            });
            c.setProps(menuSubPanelProps);
        } else if (c instanceof Button) {
            c.setProps({
                caption: c.caption ?? c.displayLabel,
                imageName: c.imageName,
                ...menuButtonProps,
                imageProps: {
                    ...c.imageProps,
                    ...menuButtonImageProps,
                }
            });
            c.addClickListener(() => this.hideContextActions(), true);
        }
        else if (c instanceof Image) {
            const convertedComponent = new Button();
            convertedComponent.setProps({
               caption: c.displayLabel,
               ...menuButtonProps,
               imageProps: {
                   name: c.name,
                   width: c.width,
                   height: c.height,
                   color: c.color,
                   rotation: c.rotation,
                   ...menuButtonImageProps,
               }
            });
            originPanel.replace(c, convertedComponent);
        } else {
            c.setProps(menuSubPanelProps);
        }
    }
}

const menuButtonProps: Partial<ButtonProps> = {
    variant: ButtonVariant.text,
    paddingTop: null,
    paddingBottom: null,
    paddingLeft: null,
    paddingRight: null,
    padding: 20,
    fontSize: "large",
    marginTop: null,
    marginBottom: null,
    marginRight: null,
    marginLeft: null,
    align: HorizontalAlignment.RIGHT,
    imageAlign: Alignment.RIGHT,
    fillRow: true,
    rowBreak: true,
    width: null,
    minWidth: null,
    maxWidth: null,
    height: null,
    minHeight: null,
    maxHeight: null,
    color: "default"
}

const menuButtonImageProps: Partial<ImageProps> = {
    marginLeft: 12,
    marginRight: null,
    marginTop: null,
    marginBottom: null,
    width: 32,
    height: 32,
    paddingTop: null,
    paddingBottom: null,
    paddingLeft: null,
    paddingRight: null,
    padding: 0
}

const menuSubPanelProps: Partial<PanelProps> = {
    padding: 0,
    margin: 0,
    width: "100%",
    rowBreak: true,
    align: HorizontalAlignment.RIGHT
}

const transitionOptions = {
    speed: 200,
    direction: Alignment.RIGHT
}

function actionPanelHeight(): number {
    return MainPageAccessor.routerHeight * 0.97;
}
